import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Timeline,
  Avatar,
  message,
  Button,
  Modal,
  Empty,
  Spin,
  Input,
  Pagination,
  DatePicker,
  TimePicker,
  Space,
  Tooltip,
  Select,
} from "antd";
import {
  UserOutlined,
  DownOutlined,
  UpOutlined,
  SearchOutlined,
  FileExcelOutlined,
  ClearOutlined
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import "./AllActivity.css";
import { getLogs, RaiseConcern, getAcountOwnerList } from "../../services/ApiService";
import { decryptObject } from "../GlobalFunction";

const { RangePicker } = DatePicker;

const groupByDate = (data) => {
  return data.reduce((acc, item) => {
    acc[item.activityDate] = acc[item.activityDate] || [];
    acc[item.activityDate].push(item);
    return acc;
  }, {});
};

const AllActivity = () => {
  const [collapsedSections, setCollapsedSections] = useState({});
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [selectActivity, setSelectActivity] = useState({});

  // Filter States
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [accountOwnerOptions, setAccountOwnerOptions] = useState([]);

  // Pagination State Variables
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const sessionData = decryptObject(sessionStorage.getItem("sessioninfo"));
  const employeeId = sessionData?.employee_id;

  useEffect(() => {
    const fetchAccountOwners = async () => {
      try {
        const res = await getAcountOwnerList();
        if (res?.responseCode === 200) {
          const options = (res?.res || []).map((owner) => ({
            label: owner.name,
            value: owner.id,
          }));
          setAccountOwnerOptions(options);
        }
      } catch (error) {
        console.error("Failed to fetch account owners:", error);
      }
    };
    fetchAccountOwners();
  }, []);

    // 🌟 UPDATED: Accepts filter parameters to request data directly from the DB
  const getActivityLog = async (pageNumber = 1, pageSize = 10, searchVal = searchText, datesVal = dateRange) => {
    setLoading(true);
    try {
      const startStr = datesVal && datesVal[0] ? datesVal[0].format("YYYY-MM-DD") : "";
      const endStr = datesVal && datesVal[1] ? datesVal[1].format("YYYY-MM-DD") : "";

      const res = await getLogs(
        employeeId, 
        sessionData?.userType, 
        pageNumber, 
        pageSize, 
        searchVal, 
        startStr, 
        endStr,
        false
      );

      if (res.responseCode === 200) {
        setLogs(res?.logList || []);
        if (res.pagination) {
          setTotalItems(res.pagination.totalItems);
          setCurrentPage(res.pagination.currentPage);
          setPerPage(res.pagination.perPage);
        }
      } else {
        message.error(res.responseMessage);
      }
    } catch (error) {
      console.error("Error fetching logs:", error);
      message.error("Failed to fetch logs.");
    } finally {
      setLoading(false);
    }
  };

  // 🌟 NEW: Automatically triggers a fresh DB fetch from page 1 when filters or page size change
  useEffect(() => {
    getActivityLog(1, perPage, searchText, dateRange);
  }, [searchText, dateRange, perPage]);

  // 🌟 UPDATED: Keeps filters intact when navigating pages
  const handlePageChange = (page, pageSize) => {
    getActivityLog(page, pageSize, searchText, dateRange);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Clear All Filters Handler
  const handleClearFilters = () => {
    setSearchText("");
    setDateRange(null);
    setSelectedTime(null);
  };

    // 🌟 UPDATED: Removed name and date filters since the DB handles them globally now
  const filteredLogs = useMemo(() => {
    if (!selectedTime) return logs;
    
    return logs.filter((item) => {
      const filterHour = selectedTime.hour();
      const filterMinute = selectedTime.minute();
      const timeString = selectedTime.format("HH:mm");
      const itemDate = new Date(item.activityDate);
      
      const isJsDateMatch = !isNaN(itemDate.getTime()) && 
                            itemDate.getHours() === filterHour && 
                            itemDate.getMinutes() === filterMinute;
      const isStringMatch = String(item.activityDate).includes(timeString);
      
      return isJsDateMatch || isStringMatch;
    });
  }, [logs, selectedTime]);


  const groupedActivities = useMemo(() => groupByDate(filteredLogs), [filteredLogs]);

  const toggleCollapse = useCallback((date) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [date]: !prev[date],
    }));
  }, []);

  // Excel Export
    // 🌟 UPDATED: Requests the entire filtered dataset bypassing pagination bounds
  const handleExportExcel = async () => {
    setLoading(true);
    try {
      const startStr = dateRange && dateRange[0] ? dateRange[0].format("YYYY-MM-DD") : "";
      const endStr = dateRange && dateRange[1] ? dateRange[1].format("YYYY-MM-DD") : "";

      const res = await getLogs(
        employeeId, 
        sessionData?.userType, 
        1, 
        999999, // Force maximum capacity threshold
        searchText, 
        startStr, 
        endStr, 
        true // Tells backend: "bypass pagination bounds"
      );

      if (res.responseCode === 200 && res.logList?.length > 0) {
        const exportData = res.logList.map((item) => ({
          "Activity ID": item.id,
          "Activity Name": item.activityName,
          "Account Owner": item.doneBy || "Unknown",
          "Activity Date": item.activityDate,
          "System Used": typeof item.systemUsed === "object" ? JSON.stringify(item.systemUsed) : item.systemUsed,
          "Concern Raised": item.concernRaise || "None",
          "Concern By": item.concernRaiseBy || "None",
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Activities");
        XLSX.writeFile(workbook, `Activity_Logs_${new Date().toISOString().slice(0, 10)}.xlsx`);
        message.success("Excel sheet downloaded successfully!");
      } else {
        message.warning("No data available to export matching these parameters.");
      }
    } catch (error) {
      console.error("Export generation error:", error);
      message.error("Failed to generate file spreadsheet.");
    } finally {
      setLoading(false);
    }
  };

  const showConcernModal = (activity) => {
    setModalContent(activity?.concernRaise || "");
    setSelectActivity(activity);
    setIsModalVisible(true);
    setIsReadOnly(!!activity?.concernRaise);
  };

  const handleSubmitConcern = async () => {
    if (!modalContent.trim()) {
      message.warning("Please enter a concern before submitting.");
      return;
    }

    try {
      const payload = {
        id: selectActivity.id,
        concern_raise: modalContent,
      };

      const res = await RaiseConcern(payload);

      if (res.responseCode === 200) {
        message.success("Concern submitted successfully.");
        setLogs((prevLogs) =>
          prevLogs.map((log) =>
            log.id === selectActivity.id
              ? { ...log, concernRaise: modalContent }
              : log
          )
        );
        setIsModalVisible(false);
        setSelectActivity({});
        setModalContent("");
        setIsReadOnly(false);
      } else {
        message.error(res.responseMessage || "Failed to submit concern.");
      }
    } catch (error) {
      console.error("Error while submitting concern:", error);
      message.error("Error while submitting concern.");
    }
  };

  const handleClose = () => {
    setIsModalVisible(false);
    setSelectActivity({});
    setModalContent("");
    setIsReadOnly(false);
  };

  const ActivityItem = ({ activity, showConcernModal }) => {
    let parsedSystemUsed;
    try {
      parsedSystemUsed = JSON.parse(activity.systemUsed);
    } catch {
      parsedSystemUsed = activity.systemUsed;
    }

    return (
      <Timeline.Item key={activity.id}>
        <div className="activity-card">
          <div className="activity-header">
            <Avatar size={30} icon={<UserOutlined />} />
            <div className="user-details">
              <div className="user-info">
                <span className="user-name">
                  {activity.doneBy || "Unknown User"}
                </span>
                <span className="activity-type">{activity.activityDate}</span>
              </div>
            </div>
          </div>

          <div className="activity-content">
            <span className="label">Activity name:</span>{" "}
            {activity.activityName}
          </div>

          <div className="activity-footer">
            <div className="tags">
              {activity?.concernRaiseBy ? (
                <span className="concernBy">
                  Concern By: {activity.concernRaiseBy}{" "}
                  <Button
                    size="small"
                    shape="round"
                    onClick={() => showConcernModal(activity)}
                  >
                    View
                  </Button>
                </span>
              ) : null}
            </div>
            <div className="system-used">
              System Used:{" "}
              {typeof parsedSystemUsed === "object"
                ? JSON.stringify(parsedSystemUsed)
                : parsedSystemUsed || "N/A"}
            </div>
          </div>
        </div>
      </Timeline.Item>
    );
  };

  return (
    <>
      <Spin
        spinning={loading}
        tip="Loading activities..."
        size="large"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh",
        }}
      >
        {/* FILTERS & PAGINATION CONTROLLER BAR */}
        <div 
          style={{ 
            position: "sticky", 
            top: 0, 
            zIndex: 10, 
            backgroundColor: "#FAFAFB", 
            padding: "1rem 2%",
            display: "flex", 
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "1rem",
            borderBottom: "1px solid #EAEAEA"
          }}
        >
          {/* Left Side: Search, Date Range, Exact Time, Clear & Export */}
          <Space wrap size="middle">
            <Select
              placeholder="Search by account owner..."
              suffixIcon={<SearchOutlined />}
              value={searchText || undefined}
              onChange={(value) => setSearchText(value || "")}
              options={accountOwnerOptions}
              showSearch
              allowClear
              optionFilterProp="label"
              style={{ width: 220 }}
            />
            <RangePicker
              value={dateRange}
              onChange={(values) => setDateRange(values)}
              allowClear
            />
            <TimePicker
              value={selectedTime}
              onChange={(time) => setSelectedTime(time)}
              format="HH:mm"
              placeholder="Exact time"
              allowClear
            />
            
            {/* Clear Filters Button */}
            {(searchText || dateRange || selectedTime) && (
              <Button 
                icon={<ClearOutlined />} 
                onClick={handleClearFilters}
              >
                Clear Filters
              </Button>
            )}

            <Tooltip title="Export Excel">
              <Button
                type="primary"
                shape="circle"
                icon={<FileExcelOutlined />}
                onClick={handleExportExcel}
                style={{ backgroundColor: "#217346", borderColor: "#217346" }}
              />
            </Tooltip>
          </Space>

          {/* Right Side: Pagination */}
          <Pagination
            current={currentPage}
            pageSize={perPage}
            total={totalItems}
            onChange={handlePageChange}
            showSizeChanger={true}
            pageSizeOptions={["10", "20", "50", "100"]}
            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} activities`}
          />
        </div>

        {/* SCROLLABLE TIMELINE BLOCK */}
        <div
          style={{ marginTop: "1rem", paddingBottom: "2rem" }}
          className="activity-timeline-container custom-scrollbar"
        >
          {Object.keys(groupedActivities).length === 0 && !loading ? (
            <Empty description="No activities found matching your filters." />
          ) : (
            <Timeline mode="left">
              {Object.entries(groupedActivities).map(([date, activities]) => (
                <div key={date}>
                  <Timeline.Item
                    label=""
                    dot={
                      <div className="date-header">
                        <strong>{date}</strong> ({activities.length})
                        <span
                          className="toggle-arrow"
                          onClick={() => toggleCollapse(date)}
                        >
                          {collapsedSections[date] ? (
                            <DownOutlined />
                          ) : (
                            <UpOutlined />
                          )}
                        </span>
                      </div>
                    }
                  />
                  {!collapsedSections[date] &&
                    activities.map((activity) => (
                      <ActivityItem
                        key={activity.id}
                        activity={activity}
                        showConcernModal={showConcernModal}
                      />
                    ))}
                </div>
              ))}
            </Timeline>
          )}
        </div>
      </Spin>

      {/* Raise Concern Modal Window */}
      <Modal
        title="Concern"
        open={isModalVisible}
        onCancel={handleClose}
        footer={
          isReadOnly ? (
            <Button type="primary" onClick={handleClose}>
              Close
            </Button>
          ) : (
            <Button type="primary" onClick={handleSubmitConcern}>
              Submit
            </Button>
          )
        }
      >
        <p className="modal-desc">
          If you notice any incorrect, missing, or suspicious information in
          this activity, please describe the issue below.
        </p>
        <Input.TextArea
          value={modalContent}
          onChange={(e) => setModalContent(e.target.value)}
          placeholder="Describe your concern about this activity, such as incorrect data, missing information, or suspicious behavior."
          rows={4}
        />
      </Modal>
    </>
  );
};

export default AllActivity;